import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ThumbsUp, MessageCircle, Flag } from 'lucide-react';
import { Comment, ContentType } from '../../types/content';
import { useTheme } from '../../contexts/ThemeContext';

interface CommentItemProps {
  comment: Comment;
  contentType: ContentType;
  onLike: () => void;
  onReply: () => void;
}

export default function CommentItem({ comment, contentType, onLike, onReply }: CommentItemProps) {
  const { theme } = useTheme();

  const renderSpecialContent = () => {
    switch (contentType) {
      case 'video':
        if ('timestamp' in comment) {
          return (
            <div className={`
              inline-block px-2 py-1 rounded text-sm mb-2
              ${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}
            `}>
              {formatTimestamp(comment.timestamp || 0)}
            </div>
          );
        }
        break;
      case 'article':
        if ('highlightedText' in comment && comment.highlightedText) {
          return (
            <blockquote className={`
              border-l-4 border-blue-500 pl-3 mb-2 italic
              ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}
            `}>
              {comment.highlightedText}
            </blockquote>
          );
        }
        break;
    }
    return null;
  };

  const formatTimestamp = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`
      p-4 rounded-lg
      ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'}
    `}>
      <div className="flex items-start space-x-3">
        <img
          src={comment.author.avatar}
          alt={comment.author.name}
          className="w-8 h-8 rounded-full ring-2 ring-blue-500/20"
        />
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            <span className={`font-medium ${
              theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
            }`}>
              {comment.author.name}
            </span>
            <span className={`text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`}>
              {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
            </span>
          </div>

          {renderSpecialContent()}

          <p className={`mt-1 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            {comment.content}
          </p>

          <div className="flex items-center space-x-4 mt-2">
            <button
              onClick={onLike}
              className={`
                flex items-center space-x-2 text-sm transition-colors
                ${theme === 'dark'
                  ? 'text-gray-400 hover:text-blue-400'
                  : 'text-gray-500 hover:text-blue-600'
                }
              `}
            >
              <ThumbsUp className="h-4 w-4" />
              <span>{comment.likes}</span>
            </button>

            <button
              onClick={onReply}
              className={`
                flex items-center space-x-2 text-sm transition-colors
                ${theme === 'dark'
                  ? 'text-gray-400 hover:text-blue-400'
                  : 'text-gray-500 hover:text-blue-600'
                }
              `}
            >
              <MessageCircle className="h-4 w-4" />
              <span>Reply</span>
            </button>

            <button
              className={`
                flex items-center space-x-2 text-sm transition-colors
                ${theme === 'dark'
                  ? 'text-gray-400 hover:text-red-400'
                  : 'text-gray-500 hover:text-red-600'
                }
              `}
            >
              <Flag className="h-4 w-4" />
              <span>Report</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}